import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ExtendedLead } from '@/components/ClientDetails/types';
import { Tarefa, PerguntaFormulario } from '@/types/tarefas';
import type { BlocoPerguntas } from '@/types/tarefas';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { formatDate, getEtapaNome, respostasParaMapa, expandirPerguntasComBlocos } from '@/utils/tarefas';
import { CheckCircle2, Clock, XCircle, FileText, Calendar, ArrowRight, CheckCircle, Plus, Pencil, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useNavigate } from 'react-router-dom';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { CRM_FUNIL_PARCERIA_PROSPECCAO } from '@/constants/crmFunil';
import { toast } from 'sonner';

interface TarefasProps {
  leadData: ExtendedLead;
  onUpdate: () => void;
  readOnly?: boolean;
}

function datetimeLocalToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Garante ano com no máximo 4 dígitos (alguns browsers permitem 5+ no datetime-local). */
function clampDatetimeLocalYearToFourDigits(value: string): string {
  if (!value) return '';
  const t = value.indexOf('T');
  if (t <= 0) return value;
  const datePart = value.slice(0, t);
  const timePart = value.slice(t);
  const hy = datePart.indexOf('-');
  if (hy <= 0) return value;
  const yearStr = datePart.slice(0, hy);
  const md = datePart.slice(hy);
  if (yearStr.length <= 4) return value;
  return `${yearStr.slice(0, 4)}${md}${timePart}`;
}

function todayYmdLocal(): string {
  const t = new Date();
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const day = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Monta `YYYY-MM-DDTHH:mm` a partir de data + hora opcional (sem horário → 18:00). */
function buildExecDatetimeLocalFromParts(ymd: string, hm: string): string {
  const d = ymd.trim();
  if (!d) return '';
  const t = hm.trim();
  const combined = t ? `${d}T${t}` : `${d}T18:00`;
  return clampDatetimeLocalYearToFourDigits(combined);
}

function isExecDatetimeInPastOrNow(localDatetime: string): boolean {
  const d = new Date(localDatetime);
  if (Number.isNaN(d.getTime())) return true;
  return d.getTime() <= Date.now();
}

function isoToDateAndTimeParts(iso: string | undefined | null): { ymd: string; hm: string } {
  if (!iso) return { ymd: '', hm: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { ymd: '', hm: '' };
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    ymd: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    hm: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/** Permite `min` no input date quando o vencimento atual já passou (senão o browser invalida o valor). */
function minYmdForVencimentoTarefaInput(currentYmd: string): string {
  const today = todayYmdLocal();
  if (!currentYmd) return today;
  return currentYmd < today ? currentYmd : today;
}

export default function Tarefas({ leadData, onUpdate, readOnly = false }: TarefasProps) {
  const { hasRole, userProfile } = useAuthorization();
  const isParceiro = hasRole('parceiro');
  const isMobile = useIsMobile();
  // Botão "Ir para Tarefa Pendente" apenas para admin ou vendedor responsável pelo lead
  const isAdmin = hasRole('admin');
  const isFinanceiro = hasRole('financeiro');
  const isVendedorResponsavel = hasRole('vendedor') && userProfile?.id === leadData.vendedor_id;
  const isParceiroDonoLead = hasRole('parceiro') && userProfile?.id === leadData.parceiro_id;
  const podeVerBotaoTarefaPendente = isAdmin || isFinanceiro;
  const processoFunilDesligado = leadData.seguirProcessoTarefas === false;
  const leadEhCliente = leadData.status === 'Cliente';
  const podeCriarTarefaAvulsa =
    Boolean(leadData.id) &&
    !readOnly &&
    (processoFunilDesligado || leadEhCliente) &&
    (isAdmin || isVendedorResponsavel || isParceiroDonoLead);
  const [tarefas, setTarefas] = useState<Tarefa[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTarefa, setSelectedTarefa] = useState<Tarefa | null>(null);
  const [showRespostasDialog, setShowRespostasDialog] = useState(false);
  const [perguntasExpandidas, setPerguntasExpandidas] = useState<PerguntaFormulario[]>([]);
  const [loadingBlocosRespostas, setLoadingBlocosRespostas] = useState(false);
  const navigate = useNavigate();

  const mostrarControleFunilTarefas =
    leadData.crmFunil !== CRM_FUNIL_PARCERIA_PROSPECCAO && leadData.status !== 'Cliente';
  const seguirFunilDoLead = leadData.seguirProcessoTarefas !== false;
  const [seguirFunilLocal, setSeguirFunilLocal] = useState(seguirFunilDoLead);
  const [salvandoSeguirFunil, setSalvandoSeguirFunil] = useState(false);

  const [dialogTarefaAvulsa, setDialogTarefaAvulsa] = useState(false);
  const [tituloAvulsa, setTituloAvulsa] = useState('');
  const [descAvulsa, setDescAvulsa] = useState('');
  const [dataExecAvulsaYmd, setDataExecAvulsaYmd] = useState('');
  const [dataExecAvulsaHm, setDataExecAvulsaHm] = useState('');
  const [dataExecAvulsaError, setDataExecAvulsaError] = useState('');
  const [salvandoAvulsa, setSalvandoAvulsa] = useState(false);
  const [editingAvulsaId, setEditingAvulsaId] = useState<string | null>(null);
  const [deleteAvulsaTarget, setDeleteAvulsaTarget] = useState<Tarefa | null>(null);
  const [deletingAvulsa, setDeletingAvulsa] = useState(false);

  useEffect(() => {
    setSeguirFunilLocal(seguirFunilDoLead);
  }, [seguirFunilDoLead, leadData.id]);

  const podeAlterarSeguirFunil = Boolean(leadData.id) && !readOnly && !isParceiro;

  const handleSeguirFunilChange = async (checked: boolean) => {
    if (!leadData.id || !podeAlterarSeguirFunil) return;
    const anterior = seguirFunilLocal;
    setSeguirFunilLocal(checked);
    setSalvandoSeguirFunil(true);
    try {
      const response = await fetchWithAuth(`/api/leads/${leadData.id}/basic`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seguirProcessoTarefas: checked }),
      });
      const json = (await response.json().catch(() => ({}))) as {
        error?: string;
        ajusteTarefasFunil?: { tarefasRemovidas?: number; tarefasCriadas?: number };
      };
      if (!response.ok) {
        throw new Error(json.error || 'Erro ao atualizar');
      }
      const adj = json.ajusteTarefasFunil;
      if (checked) {
        const n = adj?.tarefasCriadas ?? 0;
        toast.success(
          n > 0
            ? `Processo do funil ativado. ${n} tarefa(s) da etapa atual foram criadas.`
            : 'Processo do funil ativado. Nenhuma tarefa nova (já existia tarefa da etapa ou não há template).'
        );
      } else {
        const n = adj?.tarefasRemovidas ?? 0;
        toast.success(
          n > 0
            ? `Processo do funil desativado. ${n} tarefa(s) em aberto foram removidas.`
            : 'Processo do funil desativado. Não havia tarefas em aberto para remover.'
        );
      }
      onUpdate();
      await fetchTarefas();
    } catch (e) {
      console.error(e);
      setSeguirFunilLocal(anterior);
      toast.error(
        e instanceof Error ? e.message : 'Não foi possível salvar a preferência. Tente novamente.'
      );
    } finally {
      setSalvandoSeguirFunil(false);
    }
  };

  const resetFormTarefaAvulsa = () => {
    setEditingAvulsaId(null);
    setTituloAvulsa('');
    setDescAvulsa('');
    setDataExecAvulsaYmd('');
    setDataExecAvulsaHm('');
    setDataExecAvulsaError('');
  };

  const podeMutarTarefaAvulsa = (t: Tarefa) => {
    if (readOnly || t.template_id) return false;
    if (isAdmin) return true;
    if (hasRole('vendedor') && userProfile?.id && t.vendedor_id === userProfile.id) return true;
    if (isParceiroDonoLead) return true;
    return false;
  };

  const podeEditarConteudoTarefaAvulsa = (t: Tarefa) =>
    podeMutarTarefaAvulsa(t) &&
    ['pendente', 'em_andamento', 'atrasada', 'concluida_com_pendencias'].includes(t.status);

  const openNovaTarefaAvulsa = () => {
    resetFormTarefaAvulsa();
    setDialogTarefaAvulsa(true);
  };

  const openEditTarefaAvulsa = (t: Tarefa) => {
    if (t.template_id) return;
    setEditingAvulsaId(t.id);
    setTituloAvulsa(t.titulo);
    setDescAvulsa(t.descricao || '');
    const { ymd, hm } = isoToDateAndTimeParts(t.data_vencimento);
    setDataExecAvulsaYmd(ymd);
    setDataExecAvulsaHm(hm);
    setDataExecAvulsaError('');
    setDialogTarefaAvulsa(true);
  };

  const handleSalvarTarefaAvulsa = async () => {
    if (!leadData.id) return;
    const titulo = tituloAvulsa.trim();
    if (!titulo) {
      toast.error('Informe o título da tarefa.');
      return;
    }
    if (!dataExecAvulsaYmd.trim()) {
      setDataExecAvulsaError('');
      toast.error('Informe a data para execução da tarefa.');
      return;
    }
    const dataExecClamped = buildExecDatetimeLocalFromParts(dataExecAvulsaYmd, dataExecAvulsaHm);
    if (isExecDatetimeInPastOrNow(dataExecClamped)) {
      const msg =
        'Não é possível definir a execução da tarefa em uma data e hora que já passaram. Escolha um momento futuro.';
      setDataExecAvulsaError(msg);
      toast.error(msg);
      return;
    }
    setDataExecAvulsaError('');
    const dataIso = datetimeLocalToIso(dataExecClamped);
    if (!dataIso) {
      toast.error('Data de execução inválida.');
      return;
    }
    setSalvandoAvulsa(true);
    try {
      if (editingAvulsaId) {
        const res = await fetchWithAuth(`/api/tarefas/${editingAvulsaId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            titulo,
            data_vencimento: dataIso,
            descricao: descAvulsa.trim() || null,
          }),
        });
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        if (!res.ok) {
          throw new Error(typeof body.error === 'string' ? body.error : 'Erro ao atualizar tarefa');
        }
        toast.success('Tarefa atualizada.');
      } else {
        const res = await fetchWithAuth('/api/tarefas', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sem_template: true,
            lead_id: leadData.id,
            titulo,
            data_vencimento: dataIso,
            descricao: descAvulsa.trim() || undefined,
          }),
        });
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        if (!res.ok) {
          throw new Error(typeof body.error === 'string' ? body.error : 'Erro ao criar tarefa');
        }
        toast.success('Tarefa criada (sem template).');
      }
      setDialogTarefaAvulsa(false);
      resetFormTarefaAvulsa();
      await fetchTarefas();
      onUpdate();
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Não foi possível salvar a tarefa.');
    } finally {
      setSalvandoAvulsa(false);
    }
  };

  const handleDeleteTarefaAvulsa = async () => {
    if (!deleteAvulsaTarget) return;
    setDeletingAvulsa(true);
    try {
      const res = await fetchWithAuth(`/api/tarefas/${deleteAvulsaTarget.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(typeof body.error === 'string' ? body.error : 'Erro ao excluir');
      }
      toast.success('Tarefa removida.');
      setDeleteAvulsaTarget(null);
      await fetchTarefas();
      onUpdate();
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Não foi possível excluir a tarefa.');
    } finally {
      setDeletingAvulsa(false);
    }
  };

  const fetchTarefas = useCallback(async () => {
    if (!leadData.id) return;
    
    setLoading(true);
    try {
      // Buscar TODAS as tarefas do lead, independente de status ou etapa
      // A API retorna todas as tarefas quando passamos lead_id sem filtros adicionais
      const response = await fetchWithAuth(`/api/tarefas?lead_id=${leadData.id}`);
      if (!response.ok) {
        throw new Error('Erro ao buscar tarefas');
      }
      const data: Tarefa[] = await response.json();
      
      // Não aplicar nenhum filtro - mostrar TODAS as tarefas do lead
      // Ordenar por data de conclusão (mais recentes primeiro) ou data de criação
      const sorted = data.sort((a, b) => {
        const dateA = a.data_conclusao || a.data_criacao;
        const dateB = b.data_conclusao || b.data_criacao;
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      });
      
      setTarefas(sorted);
    } catch (error) {
      console.error('Erro ao carregar tarefas:', error);
    } finally {
      setLoading(false);
    }
  }, [leadData.id]);

  useEffect(() => {
    if (leadData.id) {
      fetchTarefas();
    }
  }, [leadData.id, fetchTarefas]);

  const getStatusBadge = (status: Tarefa['status']) => {
    const variants: Record<Tarefa['status'], { variant: 'default' | 'secondary' | 'destructive' | 'outline', label: string }> = {
      'pendente': { variant: 'secondary', label: 'Pendente' },
      'em_andamento': { variant: 'default', label: 'Em Andamento' },
      'concluida': { variant: 'default', label: 'Concluída' },
      'concluida_com_pendencias': { variant: 'outline', label: 'Concluída c/ Pendências' },
      'cancelada': { variant: 'destructive', label: 'Cancelada' },
      'atrasada': { variant: 'destructive', label: 'Atrasada' },
    };
    const config = variants[status] || variants.pendente;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getStatusIcon = (status: Tarefa['status']) => {
    switch (status) {
      case 'concluida':
      case 'concluida_com_pendencias':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'cancelada':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'atrasada':
        return <Clock className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  const handleTarefaClick = (tarefa: Tarefa) => {
    // Parceiros não podem visualizar detalhes das tarefas
    if (isParceiro) {
      return;
    }
    setSelectedTarefa(tarefa);
    setShowRespostasDialog(true);
  };

  // Carregar blocos e expandir perguntas quando abrir o modal de respostas (para tarefas com blocos)
  useEffect(() => {
    if (!showRespostasDialog || !selectedTarefa?.perguntas_formulario?.length) {
      if (!showRespostasDialog) {
        setPerguntasExpandidas([]);
      }
      return;
    }

    const perguntas = selectedTarefa.perguntas_formulario;
    setPerguntasExpandidas([]);

    const blocosIds = new Set<string>();
    perguntas.forEach((p) => {
      if (p.proximo_bloco_id) blocosIds.add(p.proximo_bloco_id);
      p.regras_navegacao?.forEach((r) => {
        if (r.proximo_bloco_id) blocosIds.add(r.proximo_bloco_id);
      });
    });

    if (blocosIds.size === 0) {
      setPerguntasExpandidas(perguntas);
      setLoadingBlocosRespostas(false);
      return;
    }

    setLoadingBlocosRespostas(true);
    const blocosMap = new Map<string, BlocoPerguntas>();
    Promise.all(
      Array.from(blocosIds).map(async (id) => {
        try {
          const res = await fetchWithAuth(`/api/blocos-perguntas/${id}`);
          if (res.ok) {
            const bloco: BlocoPerguntas = await res.json();
            blocosMap.set(id, bloco);
          }
        } catch (e) {
          console.error(`Erro ao buscar bloco ${id}:`, e);
        }
      })
    ).then(() => {
      const expandidas = expandirPerguntasComBlocos(perguntas, blocosMap);
      setPerguntasExpandidas(expandidas);
      setLoadingBlocosRespostas(false);
    });
  }, [showRespostasDialog, selectedTarefa?.id, selectedTarefa?.perguntas_formulario]);

  const navegarParaTarefas = () => {
    if (!isAdmin && !isFinanceiro) {
      toast.info('O quadro global de tarefas está disponível apenas para administração e financeiro.');
      return;
    }
    // Navegar para a página de Tarefas com filtro para mostrar tarefas pendentes/atrasadas/em_andamento deste lead
    // E direcionar para a tarefa pendente específica
    if (leadData.id && tarefasPendentes.length > 0) {
      // Ordenar tarefas pendentes por prioridade e data de vencimento
      // Priorizar: atrasadas > urgente > alta > outras, e depois por data de vencimento mais próxima
      const tarefaPrioritaria = [...tarefasPendentes].sort((a, b) => {
        // Primeiro, priorizar tarefas atrasadas
        if (a.status === 'atrasada' && b.status !== 'atrasada') return -1;
        if (b.status === 'atrasada' && a.status !== 'atrasada') return 1;
        
        // Depois, priorizar por prioridade
        const prioridadeOrder = { urgente: 0, alta: 1, media: 2, baixa: 3 };
        const prioridadeA = prioridadeOrder[a.prioridade as keyof typeof prioridadeOrder] ?? 3;
        const prioridadeB = prioridadeOrder[b.prioridade as keyof typeof prioridadeOrder] ?? 3;
        if (prioridadeA !== prioridadeB) return prioridadeA - prioridadeB;
        
        // Por fim, ordenar por data de vencimento (mais próxima primeiro)
        const dataA = a.data_vencimento ? new Date(a.data_vencimento).getTime() : Infinity;
        const dataB = b.data_vencimento ? new Date(b.data_vencimento).getTime() : Infinity;
        return dataA - dataB;
      })[0];
      
      // Passar parâmetros para filtrar e abrir a tarefa específica
      const params = new URLSearchParams();
      params.append('lead_id', leadData.id);
      params.append('status', 'pendente,atrasada,em_andamento');
      params.append('tarefa_id', tarefaPrioritaria.id);
      navigate(`/tarefas?${params.toString()}`);
    } else if (leadData.id) {
      // Se não houver tarefas pendentes, apenas filtrar por lead
      const params = new URLSearchParams();
      params.append('lead_id', leadData.id);
      navigate(`/tarefas?${params.toString()}`);
    } else {
      navigate('/tarefas');
    }
  };

  const tarefasPendentes = tarefas.filter(
    t => t.status === 'pendente' || t.status === 'atrasada' || t.status === 'em_andamento'
  );

  const renderRespostas = (tarefa: Tarefa, perguntasParaUsar?: PerguntaFormulario[]) => {
    if (!tarefa.respostas_formulario || tarefa.respostas_formulario.length === 0) {
      return (
        <p className="text-sm text-muted-foreground text-center py-4">
          Esta tarefa não possui respostas de formulário.
        </p>
      );
    }

    const respostasMap = respostasParaMapa(tarefa.respostas_formulario);
    const perguntas = (perguntasParaUsar && perguntasParaUsar.length > 0)
      ? perguntasParaUsar
      : (tarefa.perguntas_formulario || []);
    
    if (perguntas.length === 0) {
      return (
        <p className="text-sm text-muted-foreground text-center py-4">
          Não há perguntas disponíveis para exibir.
        </p>
      );
    }

    // Criar um mapa de perguntas para busca rápida (inclui perguntas de blocos)
    const mapaPerguntas = new Map(perguntas.map(p => [p.id, p]));
    
    // Obter todas as perguntas que têm respostas, ordenadas
    const perguntasComRespostas = Object.keys(respostasMap)
      .map(id => mapaPerguntas.get(id))
      .filter((p): p is PerguntaFormulario => Boolean(p))
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

    if (perguntasComRespostas.length === 0) {
      return (
        <p className="text-sm text-muted-foreground text-center py-4">
          Nenhuma resposta encontrada.
        </p>
      );
    }

    return (
      <div className="space-y-4 min-w-full">
        {perguntasComRespostas.map((pergunta, index) => {
          const resposta = respostasMap[pergunta.id] || '';
          return (
            <div key={pergunta.id} className="space-y-2 border-b pb-4 last:border-b-0 min-w-full">
              <div className="flex items-start gap-2">
                <span className="text-xs font-semibold text-muted-foreground min-w-[30px] flex-shrink-0">
                  {index + 1}.
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium mb-2 break-words">{pergunta.texto}</p>
                  <div className="rounded-md border bg-muted/30 p-3">
                    {pergunta.tipo_resposta === 'multipla_escolha' ? (
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                        <span className="text-sm break-words">{resposta}</span>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap break-words overflow-x-auto">{resposta}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">Carregando tarefas...</p>
      </div>
    );
  }

  // Agrupar tarefas por etapa para melhor visualização
  const tarefasPorEtapa = tarefas.reduce((acc, tarefa) => {
    const etapa = tarefa.etapa_criacao || 'outras';
    if (!acc[etapa]) {
      acc[etapa] = [];
    }
    acc[etapa].push(tarefa);
    return acc;
  }, {} as Record<string, Tarefa[]>);

  return (
    <div className="space-y-4">
      {mostrarControleFunilTarefas && (
        <div className="flex flex-row items-center justify-between rounded-lg border p-4 gap-4">
          <div className="space-y-1 min-w-0">
            <p className="text-base font-medium">Seguir processo de tarefas do funil</p>
            <p className="text-sm text-muted-foreground">
              Quando ativado, o sistema cria tarefas automáticas conforme os templates da linha do produto
              (fotovoltaico ou carregadores). Desative para acompanhar o lead no funil sem tarefas automáticas.
            </p>
          </div>
          <Switch
            className="shrink-0"
            checked={seguirFunilLocal}
            disabled={!podeAlterarSeguirFunil || salvandoSeguirFunil}
            onCheckedChange={handleSeguirFunilChange}
          />
        </div>
      )}

      {podeCriarTarefaAvulsa && (
        <Card className="border-dashed">
          <CardHeader className="py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1 min-w-0">
                <CardTitle className="text-base">Tarefa avulsa (sem template)</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {leadEhCliente
                    ? 'Cadastre tarefas de serviço ou acompanhamento para este cliente (título e data de execução). Não usam templates do funil de vendas.'
                    : 'Com o funil automático desligado, crie uma tarefa específica para este lead com título e data para execução. Ela não usa formulário de template e não é removida ao desativar o funil.'}
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                className="shrink-0"
                onClick={() => openNovaTarefaAvulsa()}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova tarefa
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}

      <div className={`flex ${isMobile ? 'flex-col' : 'items-center justify-between'} gap-3`}>
        <div>
          <h3 className="text-lg font-semibold">Tarefas do Lead</h3>
          <p className="text-sm text-muted-foreground">
            Total: {tarefas.length} tarefa(s) - Todas as etapas
          </p>
        </div>
        <div className={`flex gap-2 ${isMobile ? 'w-full' : ''}`}>
          {tarefasPendentes.length > 0 && podeVerBotaoTarefaPendente && (
            <Button 
              variant="default" 
              size="sm" 
              onClick={navegarParaTarefas}
              className={`bg-blue-600 hover:bg-blue-700 ${isMobile ? 'flex-1' : ''}`}
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Ir para Tarefa Pendente
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchTarefas}
            className={isMobile ? 'flex-1' : ''}
          >
            Atualizar
          </Button>
        </div>
      </div>

      {tarefasPendentes.length === 0 && tarefas.length > 0 && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Todas as tarefas desta etapa foram concluídas. O lead pode seguir para a próxima etapa.
          </AlertDescription>
        </Alert>
      )}

      {tarefas.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">
              Nenhuma tarefa encontrada para este lead.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(tarefasPorEtapa).map(([etapa, tarefasEtapa]) => {
            const avulsas = tarefasEtapa.filter((t) => !t.template_id);
            const comTemplate = tarefasEtapa.filter((t) => Boolean(t.template_id));
            return (
              <div key={etapa} className="space-y-3">
                <h4 className="text-md font-semibold text-muted-foreground border-b pb-2">
                  {getEtapaNome(etapa)} ({tarefasEtapa.length} tarefa(s))
                </h4>
                {avulsas.length > 0 ? (
                  <ul className="space-y-3">
                    {avulsas.map((tarefa) => (
                      <li
                        key={tarefa.id}
                        className={`rounded-lg border border-border bg-muted/20 p-4 space-y-2 ${
                          isParceiro ? '' : 'cursor-pointer hover:shadow-sm transition-shadow'
                        }`}
                        onClick={() => handleTarefaClick(tarefa)}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="space-y-1 min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground flex items-center gap-2 min-w-0">
                              {getStatusIcon(tarefa.status)}
                              <span className="break-words">{tarefa.titulo}</span>
                            </p>
                            {tarefa.descricao ? (
                              <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                                {tarefa.descricao}
                              </p>
                            ) : null}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                              <span className="inline-flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5 shrink-0" />
                                {tarefa.data_conclusao
                                  ? `Concluída em ${formatDate(tarefa.data_conclusao)}`
                                  : tarefa.data_vencimento
                                    ? `Vencimento: ${formatDate(tarefa.data_vencimento)}`
                                    : `Criada em ${formatDate(tarefa.data_criacao)}`}
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <FileText className="h-3.5 w-3.5 shrink-0" />
                                {getEtapaNome(tarefa.etapa_criacao)}
                              </span>
                            </div>
                            {tarefa.respostas_formulario && tarefa.respostas_formulario.length > 0 ? (
                              <Badge variant="outline" className="text-xs w-fit">
                                {tarefa.respostas_formulario.length} resposta(s)
                              </Badge>
                            ) : null}
                          </div>
                          <div className="flex flex-wrap items-start justify-end gap-2 shrink-0">
                            <div className="flex flex-col items-end gap-1">
                              {getStatusBadge(tarefa.status)}
                              <Badge variant="outline" className="text-[10px]">
                                Sem template
                              </Badge>
                            </div>
                            {podeMutarTarefaAvulsa(tarefa) ? (
                              <div className="flex gap-1">
                                {podeEditarConteudoTarefaAvulsa(tarefa) ? (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    aria-label="Editar tarefa"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditTarefaAvulsa(tarefa);
                                    }}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                ) : null}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  aria-label="Excluir tarefa"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteAvulsaTarget(tarefa);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {comTemplate.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {comTemplate.map((tarefa) => (
                      <Card
                        key={tarefa.id}
                        className={
                          isParceiro ? 'transition-shadow' : 'cursor-pointer hover:shadow-md transition-shadow'
                        }
                        onClick={() => handleTarefaClick(tarefa)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between gap-2">
                            <CardTitle className="text-base flex items-center gap-2 min-w-0">
                              {getStatusIcon(tarefa.status)}
                              <span className="break-words">{tarefa.titulo}</span>
                            </CardTitle>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              {getStatusBadge(tarefa.status)}
                            </div>
                          </div>
                          {tarefa.descricao && (
                            <p className="text-sm text-muted-foreground line-clamp-2">{tarefa.descricao}</p>
                          )}
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            <span>
                              {tarefa.data_conclusao
                                ? `Concluída em ${formatDate(tarefa.data_conclusao)}`
                                : tarefa.data_vencimento
                                  ? `Vencimento: ${formatDate(tarefa.data_vencimento)}`
                                  : `Criada em ${formatDate(tarefa.data_criacao)}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <FileText className="h-3 w-3" />
                            <span>{getEtapaNome(tarefa.etapa_criacao)}</span>
                          </div>
                          {tarefa.respostas_formulario && tarefa.respostas_formulario.length > 0 && (
                            <div className="pt-2 border-t">
                              <Badge variant="outline" className="text-xs">
                                {tarefa.respostas_formulario.length} resposta(s)
                              </Badge>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <Dialog
        open={dialogTarefaAvulsa}
        onOpenChange={(open) => {
          setDialogTarefaAvulsa(open);
          if (!open) resetFormTarefaAvulsa();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingAvulsaId ? 'Editar tarefa (sem template)' : 'Nova tarefa (sem template)'}</DialogTitle>
            <DialogDescription>
              {editingAvulsaId
                ? 'Altere o título, os detalhes ou a data de execução desta tarefa avulsa.'
                : 'Defina o que deve ser feito e quando. A tarefa fica atribuída ao vendedor do lead.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="tarefa-avulsa-titulo">Título</Label>
              <Input
                id="tarefa-avulsa-titulo"
                value={tituloAvulsa}
                onChange={(e) => setTituloAvulsa(e.target.value)}
                placeholder="Ex.: Ligar para confirmar interesse na proposta"
                maxLength={255}
              />
            </div>
            <div className="space-y-2">
              <Label>Data e hora para execução</Label>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Label htmlFor="tarefa-avulsa-data" className="text-xs font-normal text-muted-foreground">
                    Data
                  </Label>
                  <Input
                    id="tarefa-avulsa-data"
                    type="date"
                    min={minYmdForVencimentoTarefaInput(dataExecAvulsaYmd)}
                    max="2099-12-31"
                    aria-invalid={!!dataExecAvulsaError}
                    className={dataExecAvulsaError ? 'border-destructive' : undefined}
                    value={dataExecAvulsaYmd}
                    onChange={(e) => {
                      setDataExecAvulsaYmd(e.target.value);
                      setDataExecAvulsaError('');
                    }}
                  />
                </div>
                <div className="w-full shrink-0 space-y-1.5 sm:w-40">
                  <Label htmlFor="tarefa-avulsa-hora" className="text-xs font-normal text-muted-foreground">
                    Horário (opcional)
                  </Label>
                  <Input
                    id="tarefa-avulsa-hora"
                    type="time"
                    step={60}
                    aria-invalid={!!dataExecAvulsaError}
                    className={dataExecAvulsaError ? 'border-destructive' : undefined}
                    value={dataExecAvulsaHm}
                    onChange={(e) => {
                      setDataExecAvulsaHm(e.target.value);
                      setDataExecAvulsaError('');
                    }}
                  />
                </div>
              </div>
              {dataExecAvulsaError ? (
                <p className="text-sm text-destructive" role="alert">
                  {dataExecAvulsaError}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Ano com 4 dígitos na data. Sem horário, usa-se 18:00 no dia escolhido. O momento deve ser
                  futuro (não é permitido o passado).
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="tarefa-avulsa-desc">Detalhes (opcional)</Label>
              <Textarea
                id="tarefa-avulsa-desc"
                rows={3}
                value={descAvulsa}
                onChange={(e) => setDescAvulsa(e.target.value)}
                placeholder="Contexto, link, observações…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDialogTarefaAvulsa(false);
                resetFormTarefaAvulsa();
              }}
              disabled={salvandoAvulsa}
            >
              Cancelar
            </Button>
            <Button type="button" onClick={() => void handleSalvarTarefaAvulsa()} disabled={salvandoAvulsa}>
              {salvandoAvulsa ? 'Salvando…' : editingAvulsaId ? 'Salvar' : 'Criar tarefa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteAvulsaTarget} onOpenChange={(o) => !o && setDeleteAvulsaTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta tarefa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A tarefa avulsa será removida permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingAvulsa}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDeleteTarefaAvulsa()} disabled={deletingAvulsa}>
              {deletingAvulsa ? 'Excluindo…' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showRespostasDialog} onOpenChange={setShowRespostasDialog}>
        <DialogContent 
          className="max-w-[90vw] w-full md:max-w-2xl h-[85vh] max-h-[85vh] flex flex-col p-0 overflow-hidden shadow-2xl"
        >
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b bg-background">
            <DialogTitle className="text-xl flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              {selectedTarefa?.titulo}
            </DialogTitle>
            <DialogDescription>
              Respostas do formulário da tarefa
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <ScrollArea className="h-full">
              <div className="p-6 space-y-6">
                {loadingBlocosRespostas ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Carregando respostas...</p>
                ) : selectedTarefa ? (
                  renderRespostas(selectedTarefa, perguntasExpandidas.length > 0 ? perguntasExpandidas : undefined)
                ) : null}
              </div>
            </ScrollArea>
          </div>

          <div className="flex-shrink-0 px-6 py-4 border-t flex justify-end bg-muted/20">
            <Button variant="outline" onClick={() => setShowRespostasDialog(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

